import { Component, ElementRef, inject, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import {
    ReactiveFormsModule,
    UntypedFormArray,
    UntypedFormBuilder,
    UntypedFormGroup,
    Validators,
} from '@angular/forms';
import { UsersService } from '@app/services/users.service';
import { Router } from '@angular/router';
import { ContactsService } from '@app/services/contacts.service';
import { ContactInterface } from '@app/interfaces/contact.interface';
import { UserInterface } from '@app/interfaces/user.interface';
import { PasswordPolicyDirective } from '@app/validators/password-policy.directive';
import { MixpanelService } from '@app/services/mixpanel.service';
import { ActivationStepsEnum } from '@app/enums/activation-steps.enum';
import { takeUntil } from 'rxjs/operators';
import { emailValidator } from '@app/validators/validators';
import { RecaptchaService } from '@app/services/recaptcha-service';
import { Subject } from 'rxjs';
import { environment } from '@env/environment';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { HypoInputComponent } from '@app/components/form-components/hypo-input/hypo-input.component';
import { MortgagesService } from '@app/services/mortgages.service';
import { MortgageInterface } from '@app/interfaces/mortgage.interface';

@Component({
    selector: 'app-activation-dialog',
    templateUrl: './activation-dialog.component.html',
    styleUrls: ['./activation-dialog.component.scss'],
    imports: [
        ReactiveFormsModule,
        MatDialogModule,
        MatIconModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        MatProgressSpinnerModule,
        HypoInputComponent,
    ],
})
export class ActivationDialogComponent implements OnInit, OnDestroy {
    dialogRef = inject<MatDialogRef<ActivationDialogComponent>>(MatDialogRef);
    data = inject(MAT_DIALOG_DATA);

    public readonly usersService = inject(UsersService);
    public readonly router = inject(Router);
    public readonly fb = inject(UntypedFormBuilder);
    public readonly dialog = inject(MatDialog);
    public readonly mixpanelService = inject(MixpanelService);
    public readonly contactsService = inject(ContactsService);
    public readonly recaptchaService = inject(RecaptchaService);
    protected readonly mortgagesService = inject(MortgagesService);

    protected destroyed$: Subject<void> = new Subject<void>();

    @ViewChildren('activationInput', { read: ElementRef }) activationInputs: QueryList<ElementRef>;
    public blurContent = false; // settings for blurring content under the dialog
    public isDevelopingMode = environment.deploymentEnvironment !== 'production';
    public emailExists: boolean;
    public activationForm: UntypedFormGroup;
    public duplicityData: any = {};
    public isLoading: boolean = false;
    public contactId: string;
    public mortgage: MortgageInterface;
    public wrongCode = false;
    public successCode = false;
    public verifyApprove = false;
    user: UserInterface;
    public step = ActivationStepsEnum.enterMail;
    public showPasswords = false;
    activationCode: string;
    ActivationStepsEnum: typeof ActivationStepsEnum = ActivationStepsEnum;
    protected code: string;
    protected emailToActivate: string;

    get activationCodeForm() {
        return this.activationForm.get('activationCode') as UntypedFormArray;
    }

    public close(data?: any): void {
        this.dialogRef.close(data);
    }

    ngOnInit() {
        this.isLoading = this.data?.isLoading;
        this.mortgage = this.data?.mortgage;
        this._createActivationForm();
        this.usersService.user$.pipe(takeUntil(this.destroyed$)).subscribe((user: UserInterface) => {
            this.user = user;
        });

        this.blurContent = this.data?.blurContent;
        setTimeout(() => {
            this.dialog.afterOpened.next(this.dialogRef);
        }, 0);
    }

    ngOnDestroy(): void {
        this.destroyed$?.next();
        this.destroyed$?.complete();
    }

    passwordMatchValidator(fmg: UntypedFormGroup) {
        return fmg.get('password').value === fmg.get('confirmPassword').value ? null : { mismatch: true };
    }

    public async stepOne(): Promise<void> {
        if (!this.isLoading) {
            const email = this.activationForm.get('email').valid ? this.activationForm.get('email').value : null;

            if (email) {
                try {
                    // @API contacts.patch
                    const contact: ContactInterface = await this.contactsService.patch(this.user.contactId, {
                        email,
                        emailVerified: false,
                    });
                    this.contactId = contact?._id;

                    this.mixpanelService.track('$entered_email_for_activation', { email });

                    const gcToken = await this.recaptchaService.check('contacts/verify');
                    this.duplicityData = await this.contactsService.verify({
                        contactId: this.user.contact._id,
                        verifyEmail: true,
                        gcToken,
                        activitySource: 'banking-verification',
                    });

                    this.afterStepOne();
                } catch (error) {
                    this.resolvePromiseError(error);
                }
            }
        }
    }

    public async stepTwo() {
        if (!this.isLoading && this.activationCode?.length === 6) {
            this.isLoading = true;
            const gcToken = await this.recaptchaService.check('contacts/verify');
            let query;
            if (this.duplicityData.duplicity) {
                query = {
                    contactId: this.duplicityData.contactId,
                    emailCode: this.activationCode,
                    dealId: this.mortgage?._id,
                    gcToken,
                    activitySource: 'banking-verification',
                };
            } else {
                query = {
                    contactId: this.contactId,
                    emailCode: this.activationCode,
                    gcToken,
                    activitySource: 'banking-verification',
                };
            }

            try {
                const data = await this.contactsService.verify(query);
                this.isLoading = false;

                if (data.verified) {
                    this.mixpanelService.track('$finished_activation_via_code');
                    this.usersService.setActivated(true);
                    this.successCode = true;

                    if (this.duplicityData.duplicity || data.auth) {
                        await this.usersService.authenticateJwt(data.auth.accessToken);

                        // @API cases/mortgage.get
                        const mortgage = await this.mortgagesService.get(this.mortgage._id);

                        await this.mortgagesService.navigateToActualStep(mortgage);

                        this.close(this.mortgage);
                    } else {
                        this.close();
                    }
                } else {
                    this.wrongCode = true;
                    this.isLoading = false;
                }
            } catch (error) {
                this.resolvePromiseError(error);
                this.wrongCode = true;
                this.isLoading = false;
            }
        }
    }

    public async stepThree() {
        if (this.activationForm.valid && !this.isLoading) {
            this.isLoading = true;
            const activation = this.activationForm.getRawValue();
            const patch = {
                email: activation.email,
                password: activation.password,
            };
            try {
                // @API users.patch
                await this.usersService.patch(this.usersService.user?._id, patch);
                this.isLoading = false;
                this.close();
            } catch (error) {
                if (error.code === 409) {
                    this.emailExists = true;
                }
                this.isLoading = false;
            }
        }
    }

    resolvePromiseError(error) {
        this.isLoading = false;

        console.log(error);
    }

    public changeEmail() {
        this._createActivationForm();
        this.step = ActivationStepsEnum.enterMail;
    }

    focusActivationCodeInputs() {
        setTimeout(() => {
            this.activationInputs.toArray()?.[0]?.nativeElement.focus();
        }, 0);
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public codePasteEvent($event: ClipboardEvent, index: number) {
        const activationCode = $event.clipboardData.getData('Text').trim();
        for (let x = 0; x < this.activationCodeForm.controls.length; x++) {
            this.activationCodeForm.get(x.toString())?.patchValue(activationCode[x]);
        }
        this.checkActivationCodeLength();
        $event.preventDefault();
    }

    public codeKeyDown($event: KeyboardEvent, index: number) {
        if ($event.key === '+' || $event.key === ',' || $event.key === '-') {
            return false;
        }
        if (this.checkDigitKey($event.key)) {
            if (this.activationCodeForm.get(index.toString()).value?.length) {
                return false;
            }
        }
    }

    public codeKeyEvent($event: KeyboardEvent, index: number) {
        if ($event.key === 'Backspace') {
            this.activationCodeForm.get(index.toString())?.patchValue('');
            if (index > 0) {
                this.activationInputs.toArray()?.[index - 1]?.nativeElement.focus();
            }
        } else if (this.checkDigitKey($event.key)) {
            this.activationCodeForm.get(index.toString())?.patchValue($event.key);
            if (index < 5) {
                this.activationInputs.toArray()?.[index + 1]?.nativeElement.focus();
            }
        }
        this.checkActivationCodeLength();
    }

    protected afterStepOne() {
        this.isLoading = false;
        this.step = ActivationStepsEnum.verifyUser;
        this.focusActivationCodeInputs();
        this.checkActivationCodeLength();
    }

    protected checkDigitKey(key) {
        return (
            key === '0' ||
            key === '1' ||
            key === '2' ||
            key === '3' ||
            key === '4' ||
            key === '5' ||
            key === '6' ||
            key === '7' ||
            key === '8' ||
            key === '9'
        );
    }

    protected checkActivationCodeLength() {
        this.activationCode = this.activationCodeForm.controls
            .map((a) => (typeof a.value === 'number' || typeof a.value === 'string' ? +a.value : null))
            .filter((b) => typeof b === 'number')
            .join('');
        this.verifyApprove = this.activationCode?.length === 6;
    }

    protected _createActivationForm() {
        const email = this.usersService.user?.contact?.email
            ? this.usersService.user?.contact?.email
            : this.emailToActivate;
        this.activationForm = this.fb.group(
            {
                email: [email, [Validators.required, emailValidator]],
                password: ['', [Validators.required, PasswordPolicyDirective.validatePassword]],
                confirmPassword: ['', [Validators.required, PasswordPolicyDirective.validatePassword]],
                activationCode: this.fb.array([]),
            },
            { validator: this.passwordMatchValidator.bind(this) }
        );

        for (let x = 0; x < 6; x++) {
            const control = this.fb.control(['']);
            this.activationCodeForm.push(control);
        }
    }

    generateRandomEmail() {
        const randomString = Math.random().toString(36).substring(2, 8);
        this.activationForm.get('email').patchValue(`${randomString}@example.com`);
    }
}
